import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { getAddresses, deleteAddress, setDefaultAddress } from '../services/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import PageLayout from '../components/PageLayout';
import AddressForm from '../components/AddressForm';

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 10px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #111;
`;

const AddressesContainer = styled.div`
  background-color: #f9f9f9;
  border-radius: 4px;
  padding: 20px;
`;

const AddressCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
  }
`;

const RadioButton = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #000;
  margin-right: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
  
  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #000;
    display: ${props => props.checked ? 'block' : 'none'};
  }
`;

const AddressDetails = styled.div`
  flex: 1;
`;

const AddressName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
`;

const AddressText = styled.p`
  font-size: 14px;
  color: #666;
  margin: 5px 0;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

const NoAddressContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const NoAddressText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
`;

const AddressesPage = () => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);

  // Fetch addresses on component mount
  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAddresses();
      if (response.success) {
        setAddresses(response.addresses || []);
      } else {
        setError('Failed to load addresses');
      }
    } catch (err) {
      setError('An error occurred while fetching addresses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  const handleEditAddress = (address) => {
    setCurrentAddress(address);
    setIsAddressDialogOpen(true);
  };

  const handleDeleteAddress = async () => {
    if (!currentAddress) return;
    
    try {
      const response = await deleteAddress(currentAddress._id);
      
      if (response.success) {
        setIsDeleteDialogOpen(false);
        fetchAddresses();
      } else {
        setError(response.error || 'Failed to delete address');
      }
    } catch (err) {
      setError('An error occurred while deleting the address');
      console.error(err);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const response = await setDefaultAddress(addressId);
      
      if (response.success) {
        fetchAddresses();
      } else {
        setError(response.error || 'Failed to set default address');
      }
    } catch (err) {
      setError('An error occurred while updating the address');
      console.error(err);
    }
  };

  const openAddAddressDialog = () => {
    setCurrentAddress(null);
    setIsAddressDialogOpen(true);
  };

  const confirmDeleteAddress = (address) => {
    setCurrentAddress(address);
    setIsDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <PageLayout>
        <ContentWrapper>
          <div className="flex justify-center items-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        </ContentWrapper>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <ContentWrapper>
        <Header>
          <Title>My Saved Addresses</Title>
          <Button onClick={openAddAddressDialog}>Add New Address</Button>
        </Header>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <AddressesContainer>
          {addresses.length > 0 ? (
            addresses.map(address => (
              <AddressCard key={address._id}>
                <RadioButton 
                  checked={address.isDefault} 
                  onClick={() => !address.isDefault && handleSetDefaultAddress(address._id)}
                />
                <AddressDetails>
                  <AddressName>{user?.firstName} {user?.lastName}</AddressName>
                  <AddressText>{address.addressLine1}</AddressText>
                  {address.addressLine2 && <AddressText>{address.addressLine2}</AddressText>}
                  <AddressText>{address.townCity}, {address.postcode}</AddressText>
                  <AddressText>United Kingdom</AddressText>
                  {user?.phone && (
                    <AddressText>Phone - {user.countryCode} {user.phone}</AddressText>
                  )}
                  
                  <ButtonsContainer>
                    <Button variant="outline" onClick={() => handleEditAddress(address)}>Edit</Button>
                    <Button variant="outline" onClick={() => confirmDeleteAddress(address)}>Delete</Button>
                  </ButtonsContainer>
                </AddressDetails>
              </AddressCard>
            ))
          ) : (
            <NoAddressContainer>
              <NoAddressText>You don't have any saved addresses yet.</NoAddressText>
              <Button onClick={openAddAddressDialog}>Add New Address</Button>
            </NoAddressContainer>
          )}
        </AddressesContainer>
      </ContentWrapper>
      
      <AddressForm
        isOpen={isAddressDialogOpen}
        onClose={() => setIsAddressDialogOpen(false)}
        address={currentAddress}
        onSave={(newAddresses) => {
          setAddresses(newAddresses);
          fetchAddresses();
          setIsAddressDialogOpen(false);
        }}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete this address?</p>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="destructive" 
              onClick={handleDeleteAddress}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AddressesPage;
